/**
 * Factory that wraps a document template config with an Editor component.
 * The Editor owns field state, renders the preview form, and handles PDF generation.
 *
 * By resolving the generic at definition time (module scope), the returned
 * Editor has a fixed non-generic props type, avoiding union/intersection
 * issues when rendering dynamically selected templates.
 */
import { Loader2 } from 'lucide-react'
import type { ComponentType } from 'react'
import { createElement, useCallback, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/frontend/components/ui/button'
import type {
  DocumentClinic,
  DocumentContext,
  DocumentFormProps,
} from '@/frontend/features/printing/documents/types'
import { previewPdf } from '@/frontend/features/printing/pdf-utils'

interface DocumentConfig<TFields> {
  id: string
  label: string
  /**
   * Initial field values. Pass a factory to compute them lazily at editor
   * mount (e.g. to default a date field to "today" at the moment the form is
   * opened rather than at module load).
   */
  defaultFields: TFields | (() => TFields)
  /**
   * Clinic whose details appear in this document's header. Omit for documents
   * issued from Snoots HQ, which is the default.
   */
  clinic?: DocumentClinic
  Preview?: ComponentType<DocumentFormProps<TFields>>
  Pdf: ComponentType<{ context: DocumentContext; fields: TFields }>
}

export interface DocumentEditorProps {
  context: DocumentContext
  patientName: string
}

export function withDocumentEditor<TFields>(config: DocumentConfig<TFields>) {
  function Editor({ context, patientName }: DocumentEditorProps) {
    const [fields, setFields] = useState<TFields>(() =>
      typeof config.defaultFields === 'function'
        ? (config.defaultFields as () => TFields)()
        : config.defaultFields
    )
    const [generating, setGenerating] = useState(false)

    const handleGenerate = useCallback(async () => {
      setGenerating(true)
      try {
        const slug = patientName.toLowerCase().replace(/\s+/g, '-')
        const element = createElement(config.Pdf, { context, fields })
        await previewPdf(element, `${config.id}-${slug}.pdf`)
      } catch (err) {
        console.error('PDF generation failed:', err)
        toast.error('Failed to generate document')
      } finally {
        setGenerating(false)
      }
    }, [context, fields, patientName])

    return (
      <>
        {config.Preview ? (
          <config.Preview context={context} value={fields} onChange={setFields} />
        ) : (
          <p className="text-muted-foreground">No manual fields — ready to generate.</p>
        )}

        <div className="flex justify-end">
          <Button onClick={handleGenerate} disabled={generating}>
            {generating && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Generate PDF
          </Button>
        </div>
      </>
    )
  }

  return { ...config, Editor }
}
